'use client';

import {
  BodyLinkV3,
  LayoutGrid,
  ListItem,
  TypographyRhythm,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { EventType } from '@/analytics/types';
import useScrollTracking from '@/app/hooks/useScrollTracking';

interface TrackedLinkProps {
  readonly children: ReactNode;
  readonly href: string;
}

function TrackedLink({ children, href }: TrackedLinkProps) {
  const handleOnClick = () => {
    const pathname = new URL(href);
    void window.ouraAnalytics.track(EventType.LinkClicked, {
      cta: 'link clicked',
      location: 'cookie policy',
      path: pathname,
    });
  };

  return (
    <BodyLinkV3 href={href} target="_blank" onClick={handleOnClick}>
      {children}
    </BodyLinkV3>
  );
}

function createLink(href: string) {
  return function CreatedLink(chunks: ReactNode) {
    return <TrackedLink href={href}>{chunks}</TrackedLink>;
  };
}

const CookiePolicy = (): JSX.Element => {
  const t = useTranslations();
  useScrollTracking();

  return (
    <LayoutGrid data-cy="cookie-policy">
      <TypographyRhythm className="col-main">
        <TypographyV3 Element="h1" variant="h2">
          {t('cookie_policy_title')}
        </TypographyV3>

        {/* Section One */}
        <TypographyV3 Element="h2" variant="h4">
          {t('introduction')}
        </TypographyV3>
        <TypographyV3>
          {t.rich('cookie_policy_intro_one', {
            about: createLink('/about-us'),
          })}
        </TypographyV3>
        <TypographyV3>{t.rich('cookie_policy_intro_two')}</TypographyV3>
        <TypographyV3>
          {t.rich('cookie_policy_intro_three', {
            privacy: createLink('https://ouraring.com/privacy-policy'),
            terms: createLink('https://ouraring.com/terms-and-conditions'),
          })}
        </TypographyV3>

        {/* Section Two */}
        <TypographyV3 Element="h2" variant="h4">
          {t('our_site')}
        </TypographyV3>
        <TypographyV3>
          {t.rich('cookie_policy_our_site_copy', {
            link: () => (
              <TrackedLink href="https://ouraring.com">
                https://ouraring.com
              </TrackedLink>
            ),
          })}
        </TypographyV3>

        {/* Section Three */}
        <TypographyV3 Element="h2" variant="h4">
          {t('what_cookies_are')}
        </TypographyV3>
        <TypographyV3>{t('cookie_policy_what_cookies_are_copy')}</TypographyV3>

        {/* Section Four */}
        <TypographyV3 Element="h2" variant="h4">
          {t('why_we_use_cookies')}
        </TypographyV3>
        <TypographyV3>
          {t('cookie_policy_why_we_use_cookies_copy')}
        </TypographyV3>
        <TypographyV3>
          {t('cookie_policy_why_we_use_cookies_list_title')}
        </TypographyV3>
        <div className="flex flex-col gap-2">
          <ListItem>{t('cookie_policy_why_we_use_cookies_list_one')}</ListItem>
          <ListItem>{t('cookie_policy_why_we_use_cookies_list_two')}</ListItem>
          <ListItem>
            {t('cookie_policy_why_we_use_cookies_list_three')}
          </ListItem>
          <ListItem>{t('cookie_policy_why_we_use_cookies_list_four')}</ListItem>
          <ListItem>{t('cookie_policy_why_we_use_cookies_list_five')}</ListItem>
          <ListItem>{t('cookie_policy_why_we_use_cookies_list_six')}</ListItem>
          <ListItem>
            {t('cookie_policy_why_we_use_cookies_list_seven')}
          </ListItem>
        </div>

        {/* Section Five */}
        <TypographyV3 Element="h2" variant="h4">
          {t('types_of_cookies')}
        </TypographyV3>
        <TypographyV3>{t('cookie_policy_types_copy_one')}</TypographyV3>
        <TypographyV3>{t('cookie_policy_types_list_title')}</TypographyV3>
        <div className="flex flex-col gap-2">
          <ListItem>{t.rich('cookie_policy_types_list_one')}</ListItem>
          <ListItem>{t.rich('cookie_policy_types_list_two')}</ListItem>
          <ListItem>{t.rich('cookie_policy_types_list_three')}</ListItem>
          <ListItem>{t.rich('cookie_policy_types_list_four')}</ListItem>
        </div>
        <TypographyV3>
          {t.rich('cookie_policy_types_copy_two', {
            tool: createLink('https://optout.aboutads.info/'),
            union: createLink('https://www.youronlinechoices.eu/'),
          })}
        </TypographyV3>

        {/* Section Six */}
        <TypographyV3 Element="h2" variant="h4">
          {t('cookie_policy_consent_to_use')}
        </TypographyV3>
        <TypographyV3>{t('cookie_policy_consent_copy')}</TypographyV3>

        {/* Section Seven */}
        <TypographyV3 Element="h2" variant="h4">
          {t('managing_cookies')}
        </TypographyV3>
        <TypographyV3>
          {t('cookie_policy_managing_cookies_copy_one')}
        </TypographyV3>
        <TypographyV3>
          {t.rich('cookie_policy_managing_cookies_copy_two', {
            link: () => (
              <TrackedLink href="https://www.allaboutcookies.org">
                https://allaboutcookies.org
              </TrackedLink>
            ),
          })}
        </TypographyV3>
        <TypographyV3>
          {t('cookie_policy_managing_cookies_copy_three')}
        </TypographyV3>
        <div className="flex flex-col gap-2">
          <ListItem>
            <TrackedLink href="https://optout.networkadvertising.org/?c=1">
              Network Advertising Initiative
            </TrackedLink>{' '}
            (NAI)
          </ListItem>
          <ListItem>
            <TrackedLink href="https://optout.aboutads.info/?c=2&lang=EN">
              Digital Advertising Alliance
            </TrackedLink>{' '}
            (DAA)
          </ListItem>
          <ListItem>
            <TrackedLink href="https://www.youronlinechoices.com/">
              European Interactive Digital Advertising Alliance
            </TrackedLink>{' '}
            (EDAA)
          </ListItem>
          <ListItem>
            <TrackedLink href="https://youradchoices.ca/">
              Digital Advertising Alliance of Canada
            </TrackedLink>{' '}
            (DAAC)
          </ListItem>
        </div>
        <TypographyV3>
          {t('cookie_policy_managing_cookies_copy_four')}
        </TypographyV3>

        {/* Section Eight */}
        <TypographyV3 Element="h2" variant="h4">
          {t('your_privacy_rights')}
        </TypographyV3>
        <TypographyV3>
          {t('cookie_policy_private_rights_copy_one')}
        </TypographyV3>
        <TypographyV3>
          {t.rich('cookie_policy_private_rights_copy_two', {
            link: () => (
              <TrackedLink href="mailto:dataprotection@ouraring.com">
                dataprotection@ouraring.com
              </TrackedLink>
            ),
          })}
        </TypographyV3>

        {/* Section Nine */}
        <TypographyV3 Element="h2" variant="h4">
          {t('how_to_contact_us')}
        </TypographyV3>
        <TypographyV3>
          {t.rich('cookie_policy_contact_us_copy', {
            link: () => (
              <TrackedLink href="mailto:dataprotection@ouraring.com">
                dataprotection@ouraring.com
              </TrackedLink>
            ),
          })}
        </TypographyV3>

        {/* Section Ten */}
        <TypographyV3 Element="h2" variant="h4">
          {t('do_not_track_policy')}
        </TypographyV3>
        <TypographyV3>
          {t.rich('cookie_policy_do_not_track_copy', {
            link: createLink('https://allaboutdnt.com/'),
          })}
        </TypographyV3>

        {/* Section Eleven */}
        <TypographyV3 Element="h2" variant="h4">
          {t('changes_to_this_policy')}
        </TypographyV3>
        <TypographyV3>{t('cookie_policy_changes_copy')}</TypographyV3>
      </TypographyRhythm>
    </LayoutGrid>
  );
};

export default CookiePolicy;
